import {
	Avatar,
	Button,
	Checkbox,
	Detail,
	FlexColumn,
	FlexRow,
	Label,
	Link,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {REPORT_TYPE, ReportType} from '../../../../../constants';
import {profilePicRectSrc} from '../../../../../directApi';
import ChangeAccessForSavedReportMutation from '../../../../../mutations/change_access_for_saved_report_mutation';
import CreateReportShareMutation from '../../../../../mutations/create_report_share_mutation';
import DeleteReportShareMutation from '../../../../../mutations/delete_report_share_mutation';
import {createToast} from '../../../../shared/components/toasts/another-toast/toaster';
import {isInValidEmailFormat} from '../../../../shared/util/EmailUtil';
import Util from '../../../../shared/util/util';
import {AlreadySharedContainer, FlexContainer, SharedPersonInfoWrapper} from './ShareReportModal_style';
import {Person, ReportShare} from './ShareReportModalTypes';

type ShareReportModalProps = {
	closeModal: () => void;
	reportSharesInput: ReportShare[];
	reportId: string;
	privateAccessInput: boolean;
	reportOwner: Person;
	reportType: ReportType;
};

const ShareReportModal = ({
	closeModal,
	reportSharesInput,
	reportId,
	privateAccessInput,
	reportOwner,
	reportType,
}: ShareReportModalProps): React.JSX.Element => {
	const intl = useIntl();
	const [disableAddButton, setDisableAddButton] = useState<boolean>(true);
	const [inputErrorMessage, setInputErrorMessage] = useState<string | null>(null);
	const [reportShares, setReportShares] = useState<ReportShare[]>(reportSharesInput);
	const [newEmail, setNewEmail] = useState<string>('');
	const [privateAccess, setPrivateAccess] = useState<boolean>(privateAccessInput);

	const nonShareableReportTypes: ReportType[] = [REPORT_TYPE.UTILIZATION_REPORT, REPORT_TYPE.PROJECT_PORTFOLIO_REPORT];
	const canShareExternally = !nonShareableReportTypes.includes(reportType);

	const emailExistsInAlreadySharedPersonsList = (email: string) => {
		return reportShares.some(reportShare => reportShare.email === email);
	};

	const validateInput = (input: string) => {
		if (emailExistsInAlreadySharedPersonsList(input)) {
			setInputErrorMessage('The report has been already shared with this email.');
			setDisableAddButton(true);
		} else if (isInValidEmailFormat(input)) {
			setInputErrorMessage(null);
			setDisableAddButton(false);
		} else {
			setInputErrorMessage('You have entered an invalid email address.');
			setDisableAddButton(true);
		}
		setNewEmail(input);
	};

	const togglePrivateAccess = () => {
		const onSuccess = () => {
			if (privateAccess) {
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'shared_report.report_shared_with_organisation'}),
				});
			} else {
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'shared_report.report_made_private'}),
				});
			}

			setPrivateAccess(!privateAccess);
		};
		Util.CommitMutation(
			ChangeAccessForSavedReportMutation,
			{
				id: reportId,
				privateAccess: !privateAccess,
			},
			onSuccess
		);
	};

	const addReportShares = createSharesResponse => {
		const newReportShares = createSharesResponse.map(response => {
			return {
				email: response.node.user.email,
				sharedId: response.node.id,
			};
		});
		setReportShares([...reportShares, ...newReportShares]);
	};

	const removeReportShare = (deletedShareId: string) => {
		const filteredReportShares = reportShares.filter(reportShare => reportShare.sharedId !== deletedShareId);
		setReportShares([...filteredReportShares]);
	};

	const handleCreateShare = () => {
		Util.CommitMutation(
			CreateReportShareMutation,
			{
				reportId: reportId,
				emails: [newEmail],
			},
			data => {
				if (data.createReportShare.warnings?.length) {
					const emails = data.createReportShare.warnings
						.map(warning => warning.substring(warning.indexOf(':') + 1))
						.join(',');

					createToast({
						duration: 5000,
						message: intl.formatMessage({id: 'shared_report.person_added.error'}) + ' ' + emails,
					});
				} else {
					createToast({
						duration: 5000,
						message: intl.formatMessage({id: 'shared_report.report_shared_with_person'}, {email: newEmail}),
					});
				}
				setNewEmail('');
				setDisableAddButton(true);
				addReportShares(data.createReportShare.shareEdges);
			}
		);
	};

	const handleDeleteReportShare = (sharedId: string) => {
		Util.CommitMutation(
			DeleteReportShareMutation,
			{
				id: sharedId,
				reportId: reportId,
			},
			data => {
				removeReportShare(data.deleteReportShare.deletedShareId);
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'shared_report.person_removed'}),
				});
			}
		);
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id={'report.share'} />
			</Modal.Title>
			<Modal.Content>
				{canShareExternally ? (
					<>
						<Label>{intl.formatMessage({id: 'report.share-input-label'})}</Label>
						{inputErrorMessage && <Text color={Text.Color.error}>{inputErrorMessage}</Text>}
						<FlexContainer>
							<FlexRow>
								<TextField
									value={newEmail}
									onChange={e => {
										validateInput(e.target.value);
									}}
									data-cy="share-email"
								/>
								<Button
									data-cy="add-button"
									emphasis={'medium'}
									onPress={handleCreateShare}
									disabled={disableAddButton}
								>
									{intl.formatMessage({id: 'common.add'})}
								</Button>
							</FlexRow>
						</FlexContainer>
						<AlreadySharedContainer>
							<SharedPersonInfoWrapper>
								<FlexRow>
									<Avatar
										src={
											reportOwner.profilePictureId
												? profilePicRectSrc(reportOwner.profilePictureId)
												: undefined
										}
										size={'m'}
										name={reportOwner.fullName}
									/>
									<FlexColumn>
										<Text size={'2'}>{reportOwner.fullName}</Text>
										<Detail size={'1'}>{reportOwner.email}</Detail>
									</FlexColumn>
								</FlexRow>
								<Text>Owner</Text>
							</SharedPersonInfoWrapper>
							{reportShares
								.sort((rs1, rs2) => rs1.email.localeCompare(rs2.email))
								.map(reportShare => (
									<SharedPersonInfoWrapper>
										<FlexContainer>
											<Detail size={'1'}>{reportShare.email}</Detail>
										</FlexContainer>
										<Link size={'2'} onClick={() => handleDeleteReportShare(reportShare.sharedId)}>
											{intl.formatMessage({id: 'common.remove'})}
										</Link>
									</SharedPersonInfoWrapper>
								))}
						</AlreadySharedContainer>
					</>
				) : null}
				<Label>{intl.formatMessage({id: 'report.share-company-label'})}</Label>
				<FlexRow>
					<Checkbox data-cy={'access-checkbox'} checked={!privateAccess} onChange={togglePrivateAccess}></Checkbox>
					<Text>{intl.formatMessage({id: 'report.private_access.description'})}</Text>
				</FlexRow>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={closeModal} dataCy={'modal-btn-done'}>
					<FormattedMessage id="common.done" />
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ShareReportModal;
